import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Divider from '@components/Divider';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useModal from '@hooks/ui/useModal';
import { DiscountCouponRow } from '@models/discount';
import { OrderLine } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import useCouponControl from '@store/order/hooks/useCouponControl';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    coupon: DiscountCouponRow;
}

function DiscountCoupon({ coupon }:Props) {
    const { discounts, orderLines } = useCalculatedRequest();
    const { toggleDiscountCoupon, clearDiscountCoupon } = useCouponControl();
    const {
        id, expiresAt,
        discountMethod,
        discountTarget,
        discountValue,
        maxDiscountAmount,
        minOrderAmount, applicableItemIds, title,
    } = coupon;
    const { t } = useTranslation('order', { keyPrefix: 'discount' });
    const isChecked = discounts
        ?.map((d) => d.couponId).includes(coupon.id);
    const otherChecked = (
        discounts
            ?.filter((discount) => !!discount.couponId)?.length || 0
    ) > 0 && !isChecked;
    const { openModal, closeModal } = useModal();
    // 적용 가능한 상품
    const actualApplicableItemIds = useMemo(() => {
        const selectedItemIds = orderLines.map((item) => item.shopItemId);
        if (applicableItemIds && applicableItemIds.length !== 0) {
            return selectedItemIds.filter((itemId) => applicableItemIds?.includes(itemId));
        }
        // 1+1은 2개 이상인 상품에만 표시
        if (discountMethod === 'BOGO') {
            return selectedItemIds.filter((itemId) => {
                const { quantity } = orderLines.find((line) => line.shopItemId === itemId)!;
                return quantity >= 2;
            });
        }
        return selectedItemIds;
    }, [applicableItemIds, orderLines, discountMethod]);

    const handleSelectCoupon = () => {
        if (discountTarget === 'ITEM') {
            //  여러 상품에 적용 가능한 경우
            if (actualApplicableItemIds.length > 1) {
                selectItem();
                return;
            }
            const itemId = actualApplicableItemIds[0];
            const orderLine = orderLines
                .find((it) => it.shopItemId === itemId);
            toggleDiscountCoupon(coupon, isSelected(orderLine!), orderLine!);
        } else {
            toggleDiscountCoupon(coupon, isChecked || false);
        }
    };

    const isSelected = (orderLine: OrderLine) => {
        const { shopItemId, defaultOptions, personalOptions } = orderLine;
        const optionIds = [...defaultOptions, ...personalOptions]
            .map((it) => it.options)
            .flat()
            .map((it) => it.shopOptionId);
        return !!discounts?.find(
            (d) => d.couponId === coupon.id && d.itemId === shopItemId
          && isEqual(d.optionIds, optionIds),
        );
    };

    const handleSelectItem = (orderLine: OrderLine) => {
        closeModal();
        toggleDiscountCoupon(coupon, isSelected(orderLine), orderLine);
    };

    const selectItem = () => {
        const actualApplicableItems = orderLines
            .filter((line) => actualApplicableItemIds.includes(line.shopItemId)
          && line.couponUsable);
        openModal(<Column fullWidth spacing={20}
            align='center' >
            <Text align='center' color={colors.gray6}>
                {t('selectItem')}
            </Text>
            <Column fullWidth spacing={20}>
                {actualApplicableItems.map((item, index) => {
                    const { shopItemId, personalOptions, defaultOptions } = item;
                    const options = [...personalOptions, ...defaultOptions]
                        .map((it) => it.options)
                        .flat();
                    const optionIds = options.map((it) => it.shopOptionId);
                    const optionPrice = options
                        .map((it) => it.optionPrice * it.quantity)
                        .reduce((a, b) => a + b, 0);
                    const price = item.itemPrice + optionPrice;
                    return (
                        <Fragment key={`item ${[shopItemId, ...optionIds].join('-')}`}>
                            <Row onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectItem(item);
                            }}
                            fullWidth>
                                <div css={css`pointer-events: none;`} >
                                    <Checkbox
                                        isChecked={isSelected(item)} />
                                </div>
                                <Row spacing={12}>
                                    <Image src={item.thumbnailImage}
                                        width={60}
                                        height={60} />
                                    <Column spacing={4}>
                                        <Text>{item.itemName}</Text>
                                        <Column spacing={2}>
                                            <Text fontWeight='bold'>{price.toLocaleString()}{priceUnit}</Text>
                                            <Text size='s'>
                                                {options.map((it) => it.optionName).join(' | ')}
                                            </Text>
                                        </Column>
                                    </Column>
                                </Row>
                            </Row>
                            {(index + 1) < actualApplicableItems.length && (
                                <Divider isThin/>
                            )}
                        </Fragment>
                    );
                })}
            </Column>
            <Column fullWidth spacing={10}>
                <Button fullWidth variant='secondary'
                    onClick={closeModal}>
                    {t('cancel')}
                </Button>
            </Column>
        </Column>);
    };
    const priceUnit = usePriceUnit();
    const displayDiscountValue = useMemo(() => {
        if (discountValue) {
            if (discountMethod === 'BOGO') {
                return t('bogo');
            }
            return discountMethod === 'FIXED' ? `-${discountValue.toLocaleString()}${priceUnit}`
                : `-${discountValue}%`;
        }
        return '';
    }, [discountMethod, discountValue, t, priceUnit]);

    // 적용 가능한 상품이 없으면 미노출
    if (actualApplicableItemIds.length === 0) {
        return (<Fragment/>);
    }
    return (
        <Row fullWidth
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectCoupon();
            }}>
            <Checkbox isChecked={isChecked}
                onClick={handleSelectCoupon}
                css={css`
                          padding: 0;
                          width: 28px;
                `} />
            <Card spacing={8}>
                <Row spacing={4}>
                    <Badge >
                        {dayjs(expiresAt).format('YYYY.MM.DD')}{t('to')}
                    </Badge>
                    {discountValue && (
                        <Badge>
                            {displayDiscountValue}
                        </Badge>
                    )}
                </Row>
                <Text color={colors.gray7} size='lg'>{title}</Text>
            </Card>
        </Row>
    );
}

const Badge = styled.span`
    background-color: var(--color-gray3);
    border-radius: 6px;
    color: var(--color-gray9);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    padding: 2px 6px;
`;

const Card = styled(Column)`
    background-color: var(--color-gray1);
    border-radius: 8px;
    flex: 1;
    padding: 14px 10px 10px;
    width: 100%;
`;

export default DiscountCoupon;
